import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Load from './Load';
import PaymentStatusChip from './PaymentStatusChip';
import SolicitarReembolsoDialog from '../pages/atleta/reembolso/SolicitarReembolsoDialog';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import {
  PAGAMENTOS_ATLETA,
  PAGAMENTOS_LIGA,
  PagamentosAtletaData,
  PagamentosAtletaVars,
  PagamentosLigaData,
  PagamentosLigaVars,
  PagamentoInscricao,
  cancelarPagamentoEInscricoes,
} from '../services/pagamentoInscricaoService';

export interface PaymentsTableProps {
  externalPagamentos?: PagamentoInscricao[];
  externalLoading?: boolean;
  externalError?: boolean;
  onRefetch?: () => void;
}

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '';

const PaymentsTable: React.FC<PaymentsTableProps> = ({
  externalPagamentos,
  externalLoading,
  externalError,
  onRefetch,
}) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { currentProfile } = useProfile();
  const user = (auth?.user as any) || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);
  const atletaId = user?.atleta?.id ?? '';

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPayment, setSelectedPayment] = useState<PagamentoInscricao | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [solicitarReembolsoOpen, setSolicitarReembolsoOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const isControlled = externalPagamentos !== undefined;
  const isLigaProfile = currentProfile?.tipo === 'LIGA';
  const ligaId = currentProfile?.referenciaId ?? '';

  // Query para perfil de Liga (skip em modo controlado)
  const {
    data: ligaData,
    loading: ligaLoading,
    error: ligaError,
    refetch: ligaRefetch
  } = useQuery<PagamentosLigaData, PagamentosLigaVars>(
    PAGAMENTOS_LIGA,
    {
      variables: { ligaId, page, size: rowsPerPage },
      skip: isControlled || !isLigaProfile || !ligaId
    }
  );

  // Query para perfil de Atleta (skip em modo controlado)
  const {
    data: atletaData,
    loading: atletaLoading,
    error: atletaError,
    refetch: atletaRefetch
  } = useQuery<PagamentosAtletaData, PagamentosAtletaVars>(
    PAGAMENTOS_ATLETA,
    {
      variables: { atletaId, page, size: rowsPerPage },
      skip: isControlled || isLigaProfile || !atletaId
    }
  );

  // Dados unificados: controlado vs. não-controlado
  const loading = isControlled ? (externalLoading ?? false) : (isLigaProfile ? ligaLoading : atletaLoading);
  const error = isControlled ? externalError : (isLigaProfile ? !!ligaError : !!atletaError);
  const internalRefetch = isLigaProfile ? ligaRefetch : atletaRefetch;

  const pagamentos = useMemo(() => {
    if (isControlled) return externalPagamentos;
    const connection = isLigaProfile
      ? ligaData?.pagamentosInscricaoPorLiga
      : atletaData?.pagamentosInscricaoPorAtleta;
    return connection?.items ?? [];
  }, [isControlled, externalPagamentos, isLigaProfile, ligaData, atletaData]);

  const totalItems = useMemo(() => {
    if (isControlled) return externalPagamentos.length;
    const connection = isLigaProfile
      ? ligaData?.pagamentosInscricaoPorLiga
      : atletaData?.pagamentosInscricaoPorAtleta;
    return connection?.totalItems ?? 0;
  }, [isControlled, externalPagamentos, isLigaProfile, ligaData, atletaData]);

  const handleSort = () => {
    setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Resetar página quando o perfil mudar ou dados externos mudarem
  useEffect(() => {
    setPage(0);
  }, [isLigaProfile, ligaId, atletaId, externalPagamentos]);

  if (loading) return <Load />;
  if (error) return <div className="alert alert-danger">Erro ao carregar pagamentos.</div>;

  // Ordenação client-side
  const sortedPagamentos = pagamentos.slice().sort((a, b) => {
    const da = new Date(a.dataPagamento || a.criadoEm || '').getTime();
    const db = new Date(b.dataPagamento || b.criadoEm || '').getTime();
    return order === 'asc' ? da - db : db - da;
  });

  // Em modo controlado, paginação é client-side sobre todos os dados
  const paginatedPagamentos = isControlled
    ? sortedPagamentos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedPagamentos;

  const temReembolsoAtivo = (p: PagamentoInscricao) =>
    p.pedidosReembolso?.some((r) => r.status !== 'RECUSADO' && r.status !== 'FINALIZADO') ?? false;

  const temPedidosReembolso = (p: PagamentoInscricao) =>
    (p.pedidosReembolso?.length ?? 0) > 0;

  const temAcoesDisponiveis = (p: PagamentoInscricao) =>
    p.status === 'PENDENTE' ||
    p.status === 'GRATUITO' ||
    (p.status === 'PAGO' && !temReembolsoAtivo(p)) ||
    temPedidosReembolso(p);

  const handleReembolsoSuccess = async () => {
    setSuccessMsg('Pedido de reembolso enviado com sucesso.');
    if (isControlled && onRefetch) {
      onRefetch();
    } else {
      await internalRefetch();
    }
  };

  const handleClick = (pagamento: PagamentoInscricao) => {
    if (isLigaProfile) {
      navigate(`/athlete/payment/${pagamento.id}`);
    } else {
      if (pagamento.status === "PENDENTE") {
        navigate('/athlete/newinscriptionpayment', { state: { pagamentoId: pagamento.id } });
      } else {
        navigate(`/athlete/payment/${pagamento.id}`);
      }
    }
  };

  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, payment: PagamentoInscricao) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleCancelarPagamento = async (pagamentoId: string) => {
    if (!selectedPayment) return;
    if (!pagamentoId) return;
        const confirmar = window.confirm(
          'Tem certeza que deseja cancelar este pagamento e as inscrições ligadas a ele?'
        );
        if (!confirmar) return;

        try {
          setIsCancelling(true);
          setErrorMsg(null);
          setSuccessMsg(null);

          await cancelarPagamentoEInscricoes(pagamentoId, 'Cancelado pelo atleta');

          setSuccessMsg('Pagamento e inscrições cancelados com sucesso.');
          if (isControlled && onRefetch) {
            onRefetch();
          } else {
            await internalRefetch();
          }
        } catch (err: any) {
          console.error(err);
          setErrorMsg(err.message || 'Erro ao cancelar pagamento.');
        } finally {
          setIsCancelling(false);
        }
  };

  return (
    <>
      {errorMsg && <div className="alert alert-danger mb-3">{errorMsg}</div>}
      {successMsg && <div className="alert alert-success mb-3">{successMsg}</div>}
      <TableContainer component={Paper}>
        <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#6c757d' }}>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>#</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Torneio</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categorias</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
              <TableSortLabel active direction={order} onClick={handleSort} style={{ color: 'inherit' }}>
                Data
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Valor</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedPagamentos.map((p, index) => {
            const categorias = Array.from(new Set(p.inscricoes.map((i) => i.categoriaTorneio.nome))).join(', ');
            const torneioNome = p.inscricoes[0]?.categoriaTorneio.torneio.nome || '';
            return (
              <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleClick(p)}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{torneioNome}</TableCell>
                <TableCell>{categorias}</TableCell>
                <TableCell>{formatDate(p.dataPagamento || p.criadoEm)}</TableCell>
                <TableCell>
                  {p.descontoAplicado && p.descontoAplicado > 0 ? (
                    <>
                      <span style={{ textDecoration: 'line-through', marginRight: 4 }}>
                        {(p.valorSemDesconto ?? 0).toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </span>
                      <span className="fw-bold text-success">
                        {(p.valor ?? 0).toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}
                      </span>
                    </>
                  ) : (
                    (p.valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                  )}
                  {p.cupomAplicado && (
                    <div>
                      <span className="badge bg-info">CUPOM: {p.cupomAplicado.codigo}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <PaymentStatusChip status={p.status} />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {temAcoesDisponiveis(p) && (
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, p)}>
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Itens por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedPayment && selectedPayment.status === 'PENDENTE' && (
          <MenuItem
            disabled={isCancelling}
            onClick={(e) => {
              e.stopPropagation();
              if (selectedPayment) handleCancelarPagamento(selectedPayment.id);
              handleMenuClose();
            }}>
            {isCancelling ? 'Cancelando...' : 'Cancelar pagamento e inscricao'}
          </MenuItem>
        )}
        {selectedPayment && (selectedPayment.status === 'GRATUITO' || (selectedPayment.status === 'PAGO' && (selectedPayment.valor ?? 0) === 0 && !temReembolsoAtivo(selectedPayment))) && (
          <MenuItem
            disabled={isCancelling}
            onClick={(e) => {
              e.stopPropagation();
              if (selectedPayment) handleCancelarPagamento(selectedPayment.id);
              handleMenuClose();
            }}>
            {isCancelling ? 'Cancelando...' : 'Cancelar inscricao'}
          </MenuItem>
        )}
        {selectedPayment && selectedPayment.status === 'PAGO' && (selectedPayment.valor ?? 0) > 0 && !temReembolsoAtivo(selectedPayment) && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              setSolicitarReembolsoOpen(true);
            }}>
            Cancelar inscricao e pedir reembolso
          </MenuItem>
        )}
        {selectedPayment && temPedidosReembolso(selectedPayment) && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/athlete/payments/${selectedPayment.id}/refunds`);
              handleMenuClose();
            }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            Visualizar reembolso
          </MenuItem>
        )}
      </Menu>
    </TableContainer>
    {selectedPayment && (
      <SolicitarReembolsoDialog
        open={solicitarReembolsoOpen}
        pagamentoId={selectedPayment.id}
        onClose={() => setSolicitarReembolsoOpen(false)}
        onSuccess={handleReembolsoSuccess}
      />
    )}
    </>
  );
};

export default PaymentsTable;
